/**
 * Mock time logs for Profitability & Performance prototype.
 * — Transactional: flat $200 revenue per log (efficiency story: same $ regardless of hours).
 * — Large Project: $150 × hoursWorked (utilisation / billed hours).
 * — Completion dates are spread over the last ~90 days for date-range demos (e.g. month to date).
 */

import { normalizeEightDigitProjectNumber, pickBetterProjectTitle } from '../../utils/pwfProjectNumber'
import type { ClientType } from '../../data/top20ClientsData'

export type ProjectType = 'Transactional' | 'Large Project'

/** PWF project category ids (must match Cloud Functions). */
export const PWF_CATEGORY_TRANSACTIONAL_ID = 25
export const PWF_CATEGORY_LARGE_PROJECT_ID = 35

export interface TimeLogEntry {
  id: string
  /** Completion date (ISO yyyy-mm-dd). */
  date: string
  /** Original due date (ISO yyyy-mm-dd). On time when date ≤ dueDate. */
  dueDate: string
  /** Task start date (ISO yyyy-mm-dd) when set in PWF. */
  startDate?: string | null
  employeeName: string
  projectType: ProjectType
  /** Display project / job reference for completed-task table. */
  projectNumber: string
  /** PWF project id when available (for grouping rows). */
  projectId?: number | null
  /** Descriptive project title (not the 8-digit job number). */
  projectTitle?: string
  /** Client name for the job (mock). */
  client: string
  taskName: string
  hoursWorked: number
  /** Budgeted / allowed hours for the task (mock). */
  allocatedHours: number
  /** Hours recorded for the task (mock: ~85–100% of worked hours). */
  recordedHours: number
  hourlyCost: number
  /** Task price / revenue for this completed task. */
  revenueGenerated: number
  contactId?: number | null
  assigneeContactIds?: number[]
}

export const MOCK_EMPLOYEE_NAMES = [
  'Alex',
  'Jordan',
  'Sam',
  'Taylor',
  'Casey',
  'Morgan',
  'Riley',
  'Jamie',
  'Avery',
] as const

/** Internal cost rates per employee ($/hr). */
const HOURLY_COST_BY_EMPLOYEE: Record<(typeof MOCK_EMPLOYEE_NAMES)[number], number> = {
  Alex: 52,
  Jordan: 48,
  Sam: 55,
  Taylor: 50,
  Casey: 49,
  Morgan: 53,
  Riley: 51,
  Jamie: 47,
  Avery: 54,
}

const CLIENT_NAMES = [
  'Northside Council',
  'Harbour Developments',
  'Atlas Builders',
  'Peninsula Architects',
  'Summit Consulting',
  'Coastal Homes Pty Ltd',
  'Metro Infrastructure',
  'Riverside Holdings',
]

const TASK_NAMES = [
  'Scope review',
  'Client call',
  'Drawing updates',
  'Site visit notes',
  'RFI response',
  'Fee proposal',
  'Council submission',
  'Coordination meeting',
  'Document register',
  'As-built mark-up',
  'Variation assessment',
  'Tender review',
  'Design workshop',
  'Contract admin',
  'Progress claim check',
]

/** Seeded PRNG for stable mock data across reloads. */
function mulberry32(seed: number) {
  return function next() {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function round1(n: number) {
  return Math.round(n * 10) / 10
}

/** Due dates from ~2 weeks ago (overdue) through ~6 weeks ahead. */
function randomDueDateInWindow(rand: () => number): string {
  const d = new Date()
  const daysOffset = Math.floor(rand() * 56) - 14
  d.setDate(d.getDate() + daysOffset)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function randomStartDateBeforeDue(rand: () => number, dueDateIso: string): string {
  const due = new Date(`${dueDateIso}T12:00:00`)
  const daysBack = 1 + Math.floor(rand() * 42)
  due.setDate(due.getDate() - daysBack)
  const y = due.getFullYear()
  const m = String(due.getMonth() + 1).padStart(2, '0')
  const day = String(due.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function pickRandomActiveTaskTags(rand: () => number): string[] {
  const pool = [...ACTIVE_TASK_TAG_OPTIONS]
  const count = Math.floor(rand() * 4)
  const tags: string[] = []
  for (let i = 0; i < count && pool.length > 0; i++) {
    const idx = Math.floor(rand() * pool.length)
    tags.push(pool.splice(idx, 1)[0])
  }
  return tags.sort((a, b) => a.localeCompare(b))
}

/** Due date relative to completion: ~70% on time (due ≥ completed), rest overdue. */
function randomDueDateForCompletedTask(rand: () => number, completionDateIso: string): string {
  const d = new Date(`${completionDateIso}T12:00:00`)
  if (rand() < 0.7) {
    d.setDate(d.getDate() + Math.floor(rand() * 12))
  } else {
    d.setDate(d.getDate() - (1 + Math.floor(rand() * 18)))
  }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Spread completion dates over the last ~90 days so date-range presets have enough rows. */
function randomDateInRecentWindow(rand: () => number, maxDaysBack = 90): string {
  const d = new Date()
  d.setDate(d.getDate() - Math.floor(rand() * maxDaysBack))
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildMockTimeLogs(count: number, seed = 42): TimeLogEntry[] {
  const rand = mulberry32(seed)
  const logs: TimeLogEntry[] = []

  for (let i = 0; i < count; i++) {
    const employeeName = MOCK_EMPLOYEE_NAMES[Math.floor(rand() * MOCK_EMPLOYEE_NAMES.length)]
    const projectType: ProjectType = rand() < 0.5 ? 'Transactional' : 'Large Project'
    const hoursWorked = round1(0.5 + rand() * 7.5)
    const hourlyCost = HOURLY_COST_BY_EMPLOYEE[employeeName]

    const revenueGenerated =
      projectType === 'Transactional' ? 200 : Math.round(150 * hoursWorked * 100) / 100

    const recordedPct = 0.85 + rand() * 0.15
    const recordedHours = round1(Math.min(hoursWorked, hoursWorked * recordedPct))

    const projectNumber = `PRJ-${String(2400 + Math.floor(rand() * 7600)).padStart(5, '0')}`
    const client = CLIENT_NAMES[Math.floor(rand() * CLIENT_NAMES.length)]
    const allocatedHours = round1(
      Math.max(0.5, hoursWorked + (rand() - 0.45) * 3.5)
    )

    const date = randomDateInRecentWindow(rand)
    logs.push({
      id: `tl-${String(i + 1).padStart(3, '0')}-${Math.floor(rand() * 1e6)}`,
      date,
      dueDate: randomDueDateForCompletedTask(rand, date),
      employeeName,
      projectType,
      projectNumber,
      client,
      taskName: TASK_NAMES[Math.floor(rand() * TASK_NAMES.length)],
      hoursWorked,
      allocatedHours,
      recordedHours,
      hourlyCost,
      revenueGenerated,
    })
  }

  return logs
}

export const MOCK_TIME_LOGS: TimeLogEntry[] = buildMockTimeLogs(50)

/** Invoice line (mock) for month-to-date forecast. */
export interface InvoiceLineEntry {
  id: string
  /** Invoice date (ISO yyyy-mm-dd). */
  date: string
  amount: number
  projectNumber: string
}

function buildMockInvoiceLines(count: number, seed = 99): InvoiceLineEntry[] {
  const rand = mulberry32(seed)
  const lines: InvoiceLineEntry[] = []
  for (let i = 0; i < count; i++) {
    const amount = Math.round((180 + rand() * 4800) * 100) / 100
    lines.push({
      id: `inv-${String(i + 1).padStart(3, '0')}`,
      date: randomDateInRecentWindow(rand, 45),
      amount,
      projectNumber: `PRJ-${String(2800 + Math.floor(rand() * 7200)).padStart(5, '0')}`,
    })
  }
  return lines
}

export const MOCK_INVOICE_LINES: InvoiceLineEntry[] = buildMockInvoiceLines(45)

/** Completed on or before the due date (inclusive). */
export function isCompletedTaskDeliveredOnTime(entry: TimeLogEntry): boolean {
  return entry.date <= entry.dueDate
}

export type TasksDeliveredOnTimeStats = {
  total: number
  onTime: number
  overdue: number
  onTimePct: number
}

export function tasksDeliveredOnTimeStats(entries: readonly TimeLogEntry[]): TasksDeliveredOnTimeStats {
  let onTime = 0
  for (const e of entries) {
    if (isCompletedTaskDeliveredOnTime(e)) onTime++
  }
  const total = entries.length
  const overdue = total - onTime
  const onTimePct = total > 0 ? Math.round((onTime / total) * 1000) / 10 : 0
  return { total, onTime, overdue, onTimePct }
}

/** Status / workflow labels shown on active tasks (mock). */
export const ACTIVE_TASK_TAG_OPTIONS = [
  'Designed',
  'RFI',
  'Reviewed',
  'In progress',
  'Client review',
  'Blocked',
] as const

/** Work-in-progress task currently being worked on (not completed). */
export interface ActiveTaskEntry {
  id: string
  /** Task due date (ISO yyyy-mm-dd). */
  dueDate: string
  /** When work started (ISO yyyy-mm-dd); null if not started yet. */
  startDate: string | null
  /** Workflow tags (e.g. Designed, RFI, Reviewed). */
  tags: string[]
  employeeName: string
  projectType: ProjectType
  projectNumber: string
  projectId?: number | null
  client: string
  taskName: string
  /** Estimated / WIP value for the active task. */
  taskPrice: number
  timeRecordedHours: number
  allocatedHours: number
  contactId?: number | null
  assigneeContactIds?: number[]
}

function buildMockActiveTasks(count: number, seed = 77): ActiveTaskEntry[] {
  const rand = mulberry32(seed)
  const tasks: ActiveTaskEntry[] = []

  for (let i = 0; i < count; i++) {
    const employeeName = MOCK_EMPLOYEE_NAMES[Math.floor(rand() * MOCK_EMPLOYEE_NAMES.length)]
    const projectType: ProjectType = rand() < 0.55 ? 'Transactional' : 'Large Project'
    const timeRecordedHours = round1(0.5 + rand() * 12)
    const allocatedHours = round1(Math.max(timeRecordedHours * 0.6, timeRecordedHours + rand() * 4))
    const taskPrice = mockTaskPriceForActiveTask(projectType, timeRecordedHours)

    const dueDate = randomDueDateInWindow(rand)
    tasks.push({
      id: `at-${String(i + 1).padStart(3, '0')}-${Math.floor(rand() * 1e6)}`,
      dueDate,
      startDate: rand() < 0.18 ? null : randomStartDateBeforeDue(rand, dueDate),
      tags: pickRandomActiveTaskTags(rand),
      employeeName,
      projectType,
      projectNumber: `PRJ-${String(3100 + Math.floor(rand() * 6200)).padStart(5, '0')}`,
      client: CLIENT_NAMES[Math.floor(rand() * CLIENT_NAMES.length)],
      taskName: TASK_NAMES[Math.floor(rand() * TASK_NAMES.length)],
      taskPrice,
      timeRecordedHours,
      allocatedHours,
    })
  }

  return tasks
}

export const MOCK_ACTIVE_TASKS: ActiveTaskEntry[] = buildMockActiveTasks(38)

/** Prototype task price (not from PWF): $200 transactional, $150 × hours for large projects. */
export function mockTaskPriceForActiveTask(
  projectType: ProjectType,
  timeRecordedHours: number
): number {
  if (projectType === 'Transactional') return 200
  return Math.round(150 * Math.max(timeRecordedHours, 1) * 100) / 100
}

export function activeTaskHasStartDate(task: ActiveTaskEntry): boolean {
  const s = task.startDate?.trim() ?? ''
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
}

export function activeTaskHasDueDate(task: ActiveTaskEntry): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(task.dueDate.trim())
}

export function activeTaskHasRfiTag(task: ActiveTaskEntry): boolean {
  return task.tags.some((t) => t.trim().toLowerCase() === 'rfi')
}

export function activeTaskHasStuckTag(task: ActiveTaskEntry): boolean {
  return task.tags.some((t) => t.trim().toLowerCase() === 'stuck')
}

/** Active WIP task value in month forecast total (excludes RFI, stuck, and missing start/due). */
export function activeTaskIncludedInMonthlyForecast(task: ActiveTaskEntry): boolean {
  if (!activeTaskHasStartDate(task) || !activeTaskHasDueDate(task)) return false
  if (activeTaskHasRfiTag(task) || activeTaskHasStuckTag(task)) return false
  return true
}

/** Inclusive first and last day of the calendar month that `from` falls in. */
function forecastCalendarMonth(from: string): { start: string; end: string } | null {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(from)
  if (!match) return null
  const last = new Date(Number(match[1]), Number(match[2]), 0).getDate()
  return {
    start: `${match[1]}-${match[2]}-01`,
    end: `${match[1]}-${match[2]}-${String(last).padStart(2, '0')}`,
  }
}

export function monthToDateInvoicesTotal(range: { from: string; to: string }): number {
  let sum = 0
  for (const line of MOCK_INVOICE_LINES) {
    if (line.date >= range.from && line.date <= range.to) sum += line.amount
  }
  return Math.round(sum * 100) / 100
}

export type MonthlyForecastBreakdown = {
  monthToDateInvoices: number
  tasksToComplete: number
  rfiTasksValue: number
  total: number
  qualifyingTaskCount: number
}

export function computeMonthlyForecast(
  mtd: { from: string; to: string },
  tasks: readonly ActiveTaskEntry[] = MOCK_ACTIVE_TASKS,
  monthToDateInvoicesOverride?: number
): MonthlyForecastBreakdown {
  const monthToDateInvoices =
    monthToDateInvoicesOverride != null
      ? Math.round(monthToDateInvoicesOverride * 100) / 100
      : monthToDateInvoicesTotal(mtd)
  // To complete is only tasks due in this calendar month. October excludes November and earlier months.
  const month = forecastCalendarMonth(mtd.from)
  let tasksToComplete = 0
  let rfiTasksValue = 0
  let qualifyingTaskCount = 0
  for (const task of tasks) {
    const due = task.dueDate.trim()
    const dueThisMonth =
      month != null && /^\d{4}-\d{2}-\d{2}$/.test(due) && due >= month.start && due <= month.end
    if (activeTaskHasRfiTag(task)) {
      if (activeTaskHasStartDate(task) && activeTaskHasDueDate(task) && dueThisMonth) {
        rfiTasksValue += task.taskPrice
      }
      continue
    }
    if (!activeTaskIncludedInMonthlyForecast(task) || !dueThisMonth) continue
    tasksToComplete += task.taskPrice
    qualifyingTaskCount++
  }
  tasksToComplete = Math.round(tasksToComplete * 100) / 100
  rfiTasksValue = Math.round(rfiTasksValue * 100) / 100
  return {
    monthToDateInvoices,
    tasksToComplete,
    rfiTasksValue,
    total: Math.round((monthToDateInvoices + tasksToComplete) * 100) / 100,
    qualifyingTaskCount,
  }
}

function normalizeTagList(tags: readonly string[]): string[] {
  return [...tags].map((t) => t.trim().toLowerCase()).filter(Boolean).sort()
}

/** When selectedTags is empty, all tasks match; otherwise task tags must match the selection exactly (no extra tags). */
export function activeTaskMatchesTagFilter(task: ActiveTaskEntry, selectedTags: readonly string[]): boolean {
  if (selectedTags.length === 0) return true
  const taskTags = normalizeTagList(task.tags)
  const selected = normalizeTagList(selectedTags)
  if (taskTags.length !== selected.length) return false
  return taskTags.every((tag, i) => tag === selected[i])
}

/**
 * Demo goal targets for Key metrics cards (prototype).
 * Adjust if progress bars sit at the rails for your date filter / mock data.
 */
export const PROFITABILITY_KPI_GOALS = {
  monthToDateRevenue: 12_000,
  revenuePerPerson: 650,
  /** Labour spend ceiling for “% of budget” style progress. */
  labourBudgetCap: 9_500,
  /** Target % of completed tasks delivered on or before due date. */
  tasksDeliveredOnTimeGoalPct: 90,
  /** Month-to-date invoices + qualifying tasks to complete. */
  monthlyForecast: 15_000,
} as const

/** WIP-style open task counts by employee and project type (stable mock). */
export type OpenTasksByType = { transaction: number; largeProject: number }

function buildMockOpenTasksByEmployee(): Record<string, OpenTasksByType> {
  const rand = mulberry32(101)
  const out: Record<string, OpenTasksByType> = {}
  for (const name of MOCK_EMPLOYEE_NAMES) {
    out[name] = {
      transaction: Math.floor(rand() * 4),
      largeProject: Math.floor(rand() * 5),
    }
  }
  return out
}

export const MOCK_OPEN_TASKS_BY_EMPLOYEE: Record<string, OpenTasksByType> = buildMockOpenTasksByEmployee()

export const PROJECT_STAGES = [
  'Brief',
  'Schematic Design',
  'Design Development',
  'Construction Documentation',
  'Tender',
  'Construction',
  'Complete',
] as const

export type ProjectStage = (typeof PROJECT_STAGES)[number]

/** Project-level profitability row (mock). */
export interface ProjectProfitabilityEntry {
  id: string
  /** Numeric job reference (e.g. 23020037). */
  projectNumber: string
  projectTitle: string
  client: string
  /** PWF category — Large Project or Transactional. */
  projectType?: ProjectType
  /** PWF category id (25 transactional, 35 large project). */
  categoryId?: number | null
  /** PWF category label (e.g. from project meta). */
  category?: string | null
  /** Completion date (ISO yyyy-mm-dd). */
  completedDate: string
  /** Project start date (ISO yyyy-mm-dd) when known from PWF or tasks. */
  startDate?: string
  /** Budgeted time (minutes) for HH:MM display. */
  timeAllocatedMinutes: number
  /** Recorded time (minutes) for HH:MM display. */
  timeSpentMinutes: number
  invoiceTotal: number
}

const MOCK_PROJECT_TITLES = [
  'Resource consent processing',
  'Building consent lodgement',
  'Geotechnical assessment',
  'Stormwater design review',
  'Civil engineering — stage 2',
  'Traffic impact assessment',
  'Site servicing plans',
  'Earthworks compliance',
  'Subdivision engineering',
  'Infrastructure capacity review',
]

function minutesFromHhMm(hours: number, minutes: number): number {
  return hours * 60 + minutes
}

function buildMockProjectProfitability(count: number, seed = 88): ProjectProfitabilityEntry[] {
  const rand = mulberry32(seed)
  const projects: ProjectProfitabilityEntry[] = []
  const usedNumbers = new Set<string>()

  const seedRows: ProjectProfitabilityEntry[] = [
    {
      id: 'pp-001',
      projectNumber: '23020037',
      projectTitle: 'Waikato subdivision — resource consent & engineering',
      client: 'Alpha Waikato Ltd',
      completedDate: '2024-11-15',
      timeAllocatedMinutes: minutesFromHhMm(180, 0),
      timeSpentMinutes: minutesFromHhMm(197, 40),
      invoiceTotal: 75_939.68,
    },
    {
      id: 'pp-002',
      projectNumber: '24020787',
      projectTitle: 'Geotechnical assessment — stage 1',
      client: 'Aurae Ltd',
      completedDate: '2025-02-28',
      timeAllocatedMinutes: minutesFromHhMm(8, 0),
      timeSpentMinutes: minutesFromHhMm(9, 2),
      invoiceTotal: 12_707.5,
    },
    {
      id: 'pp-003',
      projectNumber: '24030112',
      projectTitle: 'Harbour View — civil engineering package',
      client: 'Harbour Developments',
      completedDate: '2025-01-20',
      timeAllocatedMinutes: minutesFromHhMm(42, 30),
      timeSpentMinutes: minutesFromHhMm(38, 15),
      invoiceTotal: 48_250.0,
    },
    {
      id: 'pp-004',
      projectNumber: '23019844',
      projectTitle: 'Council submission — infrastructure upgrade',
      client: 'Northside Council',
      completedDate: '2024-09-08',
      timeAllocatedMinutes: minutesFromHhMm(24, 0),
      timeSpentMinutes: minutesFromHhMm(31, 45),
      invoiceTotal: 22_400.0,
    },
    {
      id: 'pp-005',
      projectNumber: '24051209',
      projectTitle: 'Peninsula mixed-use — consent drawings',
      client: 'Peninsula Architects',
      completedDate: '2025-03-14',
      timeAllocatedMinutes: minutesFromHhMm(16, 0),
      timeSpentMinutes: minutesFromHhMm(14, 50),
      invoiceTotal: 18_960.0,
    },
    {
      id: 'pp-006',
      projectNumber: '23045501',
      projectTitle: 'Riverside stormwater modelling',
      client: 'Riverside Holdings',
      completedDate: '2024-12-02',
      timeAllocatedMinutes: minutesFromHhMm(55, 0),
      timeSpentMinutes: minutesFromHhMm(62, 10),
      invoiceTotal: 64_800.0,
    },
  ]

  for (const row of seedRows) {
    usedNumbers.add(row.projectNumber)
    projects.push(row)
  }

  for (let i = seedRows.length; i < count; i++) {
    let projectNumber = String(23_000_000 + Math.floor(rand() * 1_500_000))
    while (usedNumbers.has(projectNumber)) {
      projectNumber = String(23_000_000 + Math.floor(rand() * 1_500_000))
    }
    usedNumbers.add(projectNumber)

    const allocatedHours = 6 + rand() * 120
    const spentRatio = 0.75 + rand() * 0.45
    const timeAllocatedMinutes = Math.round(allocatedHours * 60)
    const timeSpentMinutes = Math.round(timeAllocatedMinutes * spentRatio)
    const invoiceTotal = Math.round((8_000 + rand() * 72_000) * 100) / 100

    const completed = new Date()
    completed.setDate(completed.getDate() - Math.floor(rand() * 320))
    const y = completed.getFullYear()
    const m = String(completed.getMonth() + 1).padStart(2, '0')
    const d = String(completed.getDate()).padStart(2, '0')

    projects.push({
      id: `pp-${String(i + 1).padStart(3, '0')}`,
      projectNumber,
      projectTitle: MOCK_PROJECT_TITLES[Math.floor(rand() * MOCK_PROJECT_TITLES.length)],
      client: CLIENT_NAMES[Math.floor(rand() * CLIENT_NAMES.length)],
      completedDate: `${y}-${m}-${d}`,
      timeAllocatedMinutes,
      timeSpentMinutes,
      invoiceTotal: invoiceTotal,
    })
  }

  return projects
}

export const MOCK_PROJECT_PROFITABILITY: ProjectProfitabilityEntry[] = buildMockProjectProfitability(24)

/** HH:MM from total minutes (e.g. 197:40). */
export function formatDurationHhMm(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

export function decimalHoursFromMinutes(totalMinutes: number): number {
  return totalMinutes / 60
}

/** Performance rating: time spent minus time allocated (hours). */
export function projectPerformanceScore(entry: ProjectProfitabilityEntry): number {
  const spent = decimalHoursFromMinutes(entry.timeSpentMinutes)
  const allocated = decimalHoursFromMinutes(entry.timeAllocatedMinutes)
  return Math.round((spent - allocated) * 10) / 10
}

/** Flat labour rate for project profitability table ($/hr). */
export const PROJECT_LABOUR_RATE_PER_HOUR = 400

export function projectLabourCostAtRate(
  timeSpentMinutes: number,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number {
  return Math.round(decimalHoursFromMinutes(timeSpentMinutes) * hourlyRate * 100) / 100
}

/** Invoice total minus labour cost at the flat rate. */
export function projectProfitLoss(
  invoiceTotal: number,
  timeSpentMinutes: number,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number {
  return Math.round((invoiceTotal - projectLabourCostAtRate(timeSpentMinutes, hourlyRate)) * 100) / 100
}

export function projectProfitLossForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number {
  return projectProfitLoss(entry.invoiceTotal, entry.timeSpentMinutes, hourlyRate)
}

/** Invoice total minus labour cost at allocated hours × rate. */
export function projectProfitLossFromAllocatedForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number {
  return projectProfitLoss(entry.invoiceTotal, entry.timeAllocatedMinutes, hourlyRate)
}

/** Profit / loss as a percentage of invoice total. */
export function projectMarginPct(invoiceTotal: number, profitLoss: number): number | null {
  if (invoiceTotal === 0) return null
  return Math.round((profitLoss / invoiceTotal) * 1000) / 10
}

export function projectMarginPctForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number | null {
  return projectMarginPct(entry.invoiceTotal, projectProfitLossForEntry(entry, hourlyRate))
}

/** Margin breakdown for matrix: allocated hours × rate vs invoice. */
export function projectMarginMetricsFromAllocatedForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
) {
  const hoursAllocated = decimalHoursFromMinutes(entry.timeAllocatedMinutes)
  const costOfTime = projectLabourCostAtRate(entry.timeAllocatedMinutes, hourlyRate)
  const profit = projectProfitLossFromAllocatedForEntry(entry, hourlyRate)
  const marginPct = projectMarginPctFromAllocatedForEntry(entry, hourlyRate)
  return {
    hoursAllocated,
    costOfTime,
    profit,
    marginPct,
    invoiceTotal: entry.invoiceTotal,
  }
}

/** Margin breakdown for matrix tooltips: recorded hours × rate vs invoice. */
export function projectMarginMetricsForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
) {
  const hoursRecorded = decimalHoursFromMinutes(entry.timeSpentMinutes)
  const costOfTime = projectLabourCostAtRate(entry.timeSpentMinutes, hourlyRate)
  const profit = projectProfitLossForEntry(entry, hourlyRate)
  const marginPct = projectMarginPctForEntry(entry, hourlyRate)
  return {
    hoursRecorded,
    costOfTime,
    profit,
    marginPct,
    invoiceTotal: entry.invoiceTotal,
  }
}

export function projectMarginPctFromAllocatedForEntry(
  entry: ProjectProfitabilityEntry,
  hourlyRate: number = PROJECT_LABOUR_RATE_PER_HOUR
): number | null {
  return projectMarginPct(
    entry.invoiceTotal,
    projectProfitLossFromAllocatedForEntry(entry, hourlyRate)
  )
}

/** Aggregate completed tasks by project when snapshot project rows are unavailable. */
export function buildProjectProfitabilityFromCompletedTasks(
  tasks: readonly TimeLogEntry[]
): ProjectProfitabilityEntry[] {
  const byProject = new Map<string, ProjectProfitabilityEntry>()

  for (const task of tasks) {
    const key =
      task.projectId != null
        ? `id:${task.projectId}`
        : task.projectNumber && task.projectNumber !== '—'
          ? `num:${task.projectNumber}`
          : `task:${task.id}`
    let row = byProject.get(key)
    if (!row) {
      row = {
        id: task.projectId != null ? String(task.projectId) : `agg-${key}`,
        projectNumber: task.projectNumber && task.projectNumber !== '—' ? task.projectNumber : '—',
        projectTitle: pickBetterProjectTitle(task.projectTitle, task.client, task.taskName),
        client: task.client,
        completedDate: task.date,
        timeAllocatedMinutes: 0,
        timeSpentMinutes: 0,
        invoiceTotal: 0,
      }
      byProject.set(key, row)
    }
    if (task.projectTitle?.trim() && row.projectTitle === 'Untitled') {
      row.projectTitle = pickBetterProjectTitle(task.projectTitle, task.client)
    }
    row.timeAllocatedMinutes += Math.round((task.allocatedHours ?? 0) * 60)
    row.timeSpentMinutes += Math.round((task.recordedHours ?? 0) * 60)
    row.invoiceTotal = Math.round((row.invoiceTotal + task.revenueGenerated) * 100) / 100
    if (task.date > row.completedDate) row.completedDate = task.date
  }

  return Array.from(byProject.values())
}

/** Merge task-aggregated project rows with invoiced snapshot rows. */
export function mergeProjectProfitabilitySources(
  completedTasks: readonly TimeLogEntry[],
  invoicedRows: readonly ProjectProfitabilityEntry[]
): ProjectProfitabilityEntry[] {
  const fromTasks = buildProjectProfitabilityFromCompletedTasks(completedTasks)
  if (invoicedRows.length === 0) return fromTasks

  const invoiceByProjectId = new Map(invoicedRows.map((r) => [r.id, r]))
  const invoiceByNumber = new Map<string, ProjectProfitabilityEntry>()
  for (const r of invoicedRows) {
    const num = normalizeEightDigitProjectNumber(r.projectNumber)
    if (num !== '—' && !invoiceByNumber.has(num)) invoiceByNumber.set(num, r)
    if (r.projectNumber !== '—' && !invoiceByNumber.has(r.projectNumber)) {
      invoiceByNumber.set(r.projectNumber, r)
    }
  }
  const usedInvoiceIds = new Set<string>()

  const merged = fromTasks.map((row) => {
    const inv =
      invoiceByProjectId.get(row.id) ??
      (row.projectNumber !== '—' ? invoiceByNumber.get(row.projectNumber) : undefined) ??
      (() => {
        const num = normalizeEightDigitProjectNumber(row.projectNumber)
        return num !== '—' ? invoiceByNumber.get(num) : undefined
      })()
    if (!inv) return row
    usedInvoiceIds.add(inv.id)
    return {
      ...row,
      id: inv.id,
      projectNumber: inv.projectNumber !== '—' ? inv.projectNumber : row.projectNumber,
      projectTitle: pickBetterProjectTitle(inv.projectTitle, row.projectTitle, inv.client, row.client),
      client: inv.client || row.client,
      completedDate: inv.completedDate || row.completedDate,
      invoiceTotal: inv.invoiceTotal,
      timeAllocatedMinutes: Math.max(row.timeAllocatedMinutes, inv.timeAllocatedMinutes),
      timeSpentMinutes: Math.max(row.timeSpentMinutes, inv.timeSpentMinutes),
    }
  })

  for (const inv of invoicedRows) {
    if (!usedInvoiceIds.has(inv.id)) merged.push(inv)
  }

  return merged
}

/** Snapshot window start (yyyy-mm-dd) — completed tasks & invoices from this date. */
export const PWF_SNAPSHOT_COMPLETED_FROM = '2026-05-01'

/** Matrix uses the same window as the snapshot completed-task fetch. */
export const MATRIX_TASK_STARTED_FROM = PWF_SNAPSHOT_COMPLETED_FROM

/** Project profitability views only include projects whose PWF start date is on or after this day. */
export const PROJECT_PROFITABILITY_STARTED_FROM = PWF_SNAPSHOT_COMPLETED_FROM

function projectRowKeyFromTask(task: TimeLogEntry): string {
  if (task.projectId != null) return `id:${task.projectId}`
  if (task.projectNumber && task.projectNumber !== '—') return `num:${task.projectNumber}`
  return `task:${task.id}`
}

function projectRowKeyFromEntry(row: ProjectProfitabilityEntry): string {
  if (/^\d+$/.test(row.id)) return `id:${row.id}`
  if (row.projectNumber && row.projectNumber !== '—') return `num:${row.projectNumber}`
  return row.id
}

/** Use latest completed-task date when PWF project completion date is missing or stale. */
export function enrichProjectRowsWithTaskCompletionDates(
  rows: readonly ProjectProfitabilityEntry[],
  tasks: readonly TimeLogEntry[]
): ProjectProfitabilityEntry[] {
  const latestByKey = new Map<string, string>()
  for (const task of tasks) {
    const key = projectRowKeyFromTask(task)
    const prev = latestByKey.get(key)
    if (!prev || task.date > prev) latestByKey.set(key, task.date)
  }
  return rows.map((row) => {
    const latest = latestByKey.get(projectRowKeyFromEntry(row))
    if (latest && (!row.completedDate || latest > row.completedDate)) {
      return { ...row, completedDate: latest }
    }
    return row
  })
}

export function filterProjectsCompletedInRange(
  rows: readonly ProjectProfitabilityEntry[],
  from: string,
  to: string
): ProjectProfitabilityEntry[] {
  return rows.filter((row) => {
    const d = row.completedDate?.trim()
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return false
    if (from && d < from) return false
    if (to && d > to) return false
    return true
  })
}

export function enrichProjectRowsForProfitabilityFilters(
  rows: readonly ProjectProfitabilityEntry[],
  tasks: readonly TimeLogEntry[]
): ProjectProfitabilityEntry[] {
  return enrichProjectRowsWithTaskCompletionDates(rows, tasks)
}

export function filterProjectsStartedInRange(
  rows: readonly ProjectProfitabilityEntry[],
  from: string,
  to: string
): ProjectProfitabilityEntry[] {
  return rows.filter((row) => {
    const d = row.startDate?.trim()
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return false
    if (from && d < from) return false
    if (to && d > to) return false
    return true
  })
}

export function countProjectsMissingStartDate(
  rows: readonly ProjectProfitabilityEntry[]
): number {
  return rows.filter((row) => {
    const d = row.startDate?.trim()
    return !d || !/^\d{4}-\d{2}-\d{2}$/.test(d)
  }).length
}

export type AverageProfitMarginResult = {
  marginPct: number | null
  projectCount: number
  invoicedTotal: number
}

function resolveProjectTypeForMargin(row: ProjectProfitabilityEntry): ProjectType | null {
  return (
    projectTypeFromCategoryId(row.categoryId) ??
    projectTypeFromCategoryLabel(row.category) ??
    row.projectType ??
    null
  )
}

/**
 * Weighted average margin (total profit ÷ total invoiced ex GST) for invoiced projects
 * whose PWF start date falls on or after `startedFrom`. Optionally filter by project type.
 */
export function averageProfitMarginForProjectsStartedSince(
  rows: readonly ProjectProfitabilityEntry[],
  startedFrom: string,
  labourRatePerHour: number = PROJECT_LABOUR_RATE_PER_HOUR,
  projectType?: ProjectType
): AverageProfitMarginResult {
  const eligible = filterProjectsStartedInRange(
    rows.filter((row) => {
      if (row.invoiceTotal <= 0 || isExcludedProjectProfitabilityClient(row.client)) return false
      if (projectType && resolveProjectTypeForMargin(row) !== projectType) return false
      return true
    }),
    startedFrom,
    ''
  )

  let totalInvoice = 0
  let totalProfit = 0
  let projectCount = 0

  for (const row of eligible) {
    const metrics = projectMarginMetricsForEntry(row, labourRatePerHour)
    if (metrics.marginPct == null || metrics.invoiceTotal <= 0) continue
    totalInvoice += metrics.invoiceTotal
    totalProfit += metrics.profit
    projectCount += 1
  }

  if (projectCount === 0 || totalInvoice <= 0) {
    return { marginPct: null, projectCount: 0, invoicedTotal: 0 }
  }

  return {
    marginPct: projectMarginPct(totalInvoice, totalProfit),
    projectCount,
    invoicedTotal: Math.round(totalInvoice * 100) / 100,
  }
}

const MARGIN_CLIENT_TYPES: ClientType[] = [
  'Council',
  'Developer',
  'Builder',
  'Architect',
  'Consultant',
]

/** Infer client segment from company name (PWF has no client-type field). */
export function inferClientTypeFromName(clientName: string): ClientType | null {
  const n = clientName.trim().toLowerCase()
  if (!n) return null
  if (/\bcouncil\b/.test(n)) return 'Council'
  if (/\barchitect/.test(n)) return 'Architect'
  if (/\bbuilder|\bconstruction\b|\bbuild(?:ers?)?\b/.test(n)) return 'Builder'
  if (/\bconsult(ant|ing)\b/.test(n)) return 'Consultant'
  if (/\bdevelop(er|ment)\b/.test(n)) return 'Developer'
  return null
}

export type MarginByClientTypeRow = {
  type: ClientType
  marginPct: number | null
  projectCount: number
  revenue: number
}

export function buildMarginByClientTypeFromProjects(
  rows: readonly ProjectProfitabilityEntry[],
  startedFrom: string,
  labourRatePerHour: number = PROJECT_LABOUR_RATE_PER_HOUR
): MarginByClientTypeRow[] {
  const eligible = filterProjectsStartedInRange(
    rows.filter((row) => row.invoiceTotal > 0 && !isExcludedProjectProfitabilityClient(row.client)),
    startedFrom,
    ''
  )

  const byType = new Map<ClientType, { invoice: number; profit: number; count: number }>()
  for (const type of MARGIN_CLIENT_TYPES) {
    byType.set(type, { invoice: 0, profit: 0, count: 0 })
  }

  for (const row of eligible) {
    const type = inferClientTypeFromName(row.client)
    if (!type) continue
    const metrics = projectMarginMetricsForEntry(row, labourRatePerHour)
    if (metrics.marginPct == null || metrics.invoiceTotal <= 0) continue
    const acc = byType.get(type)!
    acc.invoice += metrics.invoiceTotal
    acc.profit += metrics.profit
    acc.count += 1
  }

  return MARGIN_CLIENT_TYPES.map((type) => {
    const acc = byType.get(type)!
    return {
      type,
      marginPct:
        acc.count > 0 && acc.invoice > 0 ? projectMarginPct(acc.invoice, acc.profit) : null,
      projectCount: acc.count,
      revenue: Math.round(acc.invoice * 100) / 100,
    }
  })
}

export type ProjectMarginRow = {
  id: string
  name: string
  client: string
  budgetAlloc: number
  timeSpentHours: number
  invoicedTotal: number
  marginPct: number | null
}

/** @deprecated Use ProjectMarginRow */
export type LargeProjectMarginRow = ProjectMarginRow

export function buildProjectMarginRowsByType(
  rows: readonly ProjectProfitabilityEntry[],
  startedFrom: string,
  projectType: ProjectType,
  labourRatePerHour: number = PROJECT_LABOUR_RATE_PER_HOUR
): ProjectMarginRow[] {
  const eligible = filterProjectsStartedInRange(
    rows.filter((row) => {
      if (row.invoiceTotal <= 0 || isExcludedProjectProfitabilityClient(row.client)) return false
      return resolveProjectTypeForMargin(row) === projectType
    }),
    startedFrom,
    ''
  )

  return eligible
    .map((row) => {
      const metrics = projectMarginMetricsForEntry(row, labourRatePerHour)
      const hoursAllocated = decimalHoursFromMinutes(row.timeAllocatedMinutes)
      const displayName = row.projectTitle?.trim() || row.projectNumber
      return {
        id: row.id,
        name: displayName,
        client: row.client,
        budgetAlloc: Math.round(hoursAllocated * labourRatePerHour * 100) / 100,
        timeSpentHours: Math.round(decimalHoursFromMinutes(row.timeSpentMinutes) * 10) / 10,
        invoicedTotal: row.invoiceTotal,
        marginPct: metrics.marginPct,
      }
    })
    .sort((a, b) => b.invoicedTotal - a.invoicedTotal || a.name.localeCompare(b.name))
}

export function buildTransactionalProjectMarginRows(
  rows: readonly ProjectProfitabilityEntry[],
  startedFrom: string,
  labourRatePerHour: number = PROJECT_LABOUR_RATE_PER_HOUR
): ProjectMarginRow[] {
  return buildProjectMarginRowsByType(rows, startedFrom, 'Transactional', labourRatePerHour)
}

export function buildLargeProjectMarginRows(
  rows: readonly ProjectProfitabilityEntry[],
  startedFrom: string,
  labourRatePerHour: number = PROJECT_LABOUR_RATE_PER_HOUR
): ProjectMarginRow[] {
  return buildProjectMarginRowsByType(rows, startedFrom, 'Large Project', labourRatePerHour)
}

/** Clients omitted from the project profitability matrix. */
export function isExcludedProjectProfitabilityClient(client: string): boolean {
  const c = client.trim().toLowerCase()
  if (!c || c === '—') return false
  if (c.includes('brevity')) return true
  return c === 'bvt' || c.startsWith('bvt ') || c.endsWith(' bvt') || c.includes(' bvt ')
}

export function taskMatchesInvoice(
  task: TimeLogEntry,
  inv: ProjectProfitabilityEntry
): boolean {
  if (task.projectId != null && String(task.projectId) === String(inv.id)) return true
  const taskNum = normalizeEightDigitProjectNumber(task.projectNumber)
  const invNum = normalizeEightDigitProjectNumber(inv.projectNumber)
  return taskNum !== '—' && invNum !== '—' && taskNum === invNum
}

/** Best available 8-digit job number for a matrix row (snapshot row or matching tasks). */
export function resolveMatrixProjectNumber(
  row: ProjectProfitabilityEntry,
  completedTasks: readonly TimeLogEntry[]
): string {
  for (const value of [row.projectNumber, row.projectTitle]) {
    const num = normalizeEightDigitProjectNumber(value)
    if (num !== '—') return num
  }
  for (const task of completedTasks) {
    if (!taskMatchesInvoice(task, row)) continue
    for (const value of [task.projectNumber, task.projectTitle, task.taskName]) {
      const num = normalizeEightDigitProjectNumber(value)
      if (num !== '—') return num
    }
  }
  const raw = row.projectNumber?.trim()
  if (raw && raw !== '—') return raw
  for (const task of completedTasks) {
    if (!taskMatchesInvoice(task, row)) continue
    const rawTask = task.projectNumber?.trim()
    if (rawTask && rawTask !== '—') return rawTask
  }
  return row.id
}

/** PWF project id for deep links (row id or matching completed task). */
export function resolveMatrixProjectId(
  row: ProjectProfitabilityEntry,
  completedTasks: readonly TimeLogEntry[]
): number | null {
  const rowId = String(row.id).trim()
  if (/^\d+$/.test(rowId)) return Number(rowId)
  for (const task of completedTasks) {
    if (!taskMatchesInvoice(task, row)) continue
    if (task.projectId != null) return Number(task.projectId)
  }
  return null
}

/** Infer project type from PWF category id. */
export function projectTypeFromCategoryId(
  categoryId: number | null | undefined
): ProjectType | null {
  if (categoryId === PWF_CATEGORY_LARGE_PROJECT_ID) return 'Large Project'
  if (categoryId === PWF_CATEGORY_TRANSACTIONAL_ID) return 'Transactional'
  return null
}

/** Infer project type from a PWF category label. */
export function projectTypeFromCategoryLabel(
  category: string | null | undefined
): ProjectType | null {
  if (!category) return null
  const n = category.toLowerCase().trim()
  if (n.includes('transactional')) return 'Transactional'
  if (n.includes('large')) return 'Large Project'
  return null
}

/** Build best-known project type per PWF project id from snapshot rows and tasks. */
function buildProjectTypeByProjectId(
  completedTasks: readonly TimeLogEntry[],
  invoicedRows: readonly ProjectProfitabilityEntry[],
  activeTasks: readonly ActiveTaskEntry[] = []
): Map<string, ProjectType> {
  const byProjectId = new Map<string, ProjectType>()

  const setType = (projectId: string | number | null | undefined, type: ProjectType) => {
    if (projectId == null) return
    const id = String(projectId)
    const cur = byProjectId.get(id)
    if (type === 'Large Project' || !cur) {
      byProjectId.set(id, type)
    }
  }

  for (const row of invoicedRows) {
    const fromCatId = projectTypeFromCategoryId(row.categoryId)
    if (fromCatId) setType(row.id, fromCatId)
    const fromCategory = projectTypeFromCategoryLabel(row.category)
    if (fromCategory) setType(row.id, fromCategory)
    if (row.projectType) setType(row.id, row.projectType)
  }

  for (const task of completedTasks) {
    setType(task.projectId, task.projectType)
  }
  for (const task of activeTasks) {
    setType(task.projectId, task.projectType)
  }

  return byProjectId
}

/** PWF category for a matrix row (snapshot row or matching completed tasks). */
export function resolveMatrixProjectType(
  row: ProjectProfitabilityEntry,
  completedTasks: readonly TimeLogEntry[],
  typeByProjectId?: ReadonlyMap<string, ProjectType>
): ProjectType | null {
  const fromCatId = projectTypeFromCategoryId(row.categoryId)
  if (fromCatId) return fromCatId

  const fromCategory = projectTypeFromCategoryLabel(row.category)
  if (fromCategory) return fromCategory

  if (typeByProjectId?.get(row.id) === 'Large Project') return 'Large Project'

  if (row.projectType === 'Large Project') return 'Large Project'

  const counts: Record<ProjectType, number> = { Transactional: 0, 'Large Project': 0 }
  for (const task of completedTasks) {
    if (!taskMatchesInvoice(task, row)) continue
    counts[task.projectType]++
  }
  if (counts['Large Project'] > 0) return 'Large Project'
  if (counts['Large Project'] > counts.Transactional) return 'Large Project'

  if (typeByProjectId?.get(row.id)) return typeByProjectId.get(row.id)!

  if (row.projectType === 'Transactional') return 'Transactional'
  if (counts.Transactional > 0) return 'Transactional'
  return null
}

export function resolveMatrixDotProjectType(
  project: ProjectProfitabilityEntry,
  completedTasks: readonly TimeLogEntry[],
  activeTasks: readonly ActiveTaskEntry[] = [],
  invoicedRows: readonly ProjectProfitabilityEntry[] = []
): ProjectType | null {
  const typeByProjectId = buildProjectTypeByProjectId(
    completedTasks,
    invoicedRows.length ? invoicedRows : [project],
    activeTasks
  )
  return (
    projectTypeFromCategoryId(project.categoryId) ??
    resolveMatrixProjectType(project, completedTasks, typeByProjectId)
  )
}

/**
 * Tracked time comes from snapshot `timeSpentMinutes` (all-time per project via PWF).
 */
export function buildProfitabilityMatrixProjects(
  completedTasks: readonly TimeLogEntry[],
  invoicedRows: readonly ProjectProfitabilityEntry[],
  activeTasks: readonly ActiveTaskEntry[] = []
): ProjectProfitabilityEntry[] {
  const typeByProjectId = buildProjectTypeByProjectId(
    completedTasks,
    invoicedRows,
    activeTasks
  )

  return invoicedRows
    .filter((row) => row.invoiceTotal > 0)
    .filter((row) => !isExcludedProjectProfitabilityClient(row.client))
    .map((row) => ({
      ...row,
      projectNumber: resolveMatrixProjectNumber(row, completedTasks),
      projectType: resolveMatrixProjectType(row, completedTasks, typeByProjectId) ?? undefined,
    }))
    .sort(
      (a, b) =>
        (b.completedDate ?? '').localeCompare(a.completedDate ?? '') ||
        a.projectNumber.localeCompare(b.projectNumber)
    )
}

/** Snapshot counts useful when the matrix is empty. */
export function profitabilityMatrixStats(
  completedTasks: readonly TimeLogEntry[],
  invoicedRows: readonly ProjectProfitabilityEntry[]
): {
  completedTaskCount: number
  invoicedProjectCount: number
  excludedClientCount: number
  matrixProjectCount: number
} {
  const invoiced = invoicedRows.filter((r) => r.invoiceTotal > 0)
  const excludedClientCount = invoiced.filter((r) =>
    isExcludedProjectProfitabilityClient(r.client)
  ).length
  const matrix = buildProfitabilityMatrixProjects(completedTasks, invoicedRows)
  return {
    completedTaskCount: completedTasks.length,
    invoicedProjectCount: invoiced.length,
    excludedClientCount,
    matrixProjectCount: matrix.length,
  }
}

export type ClientProfitabilityProjectDetail = {
  id: string
  projectNumber: string
  projectTitle: string
  invoiceTotal: number
  hoursRecorded: number
  profit: number
  marginPct: number | null
  projectType: ProjectType | null
  completedDate: string
  startDate: string
}

export type ClientProfitabilityRow = {
  client: string
  projectCount: number
  largeProjectCount: number
  transactionalProjectCount: number
  invoiceTotal: number
  hoursRecorded: number
  costOfTime: number
  profit: number
  marginPct: number | null
  latestCompletedDate: string | null
  projects: ClientProfitabilityProjectDetail[]
}

/** Roll up matrix-eligible projects to one row per client. */
export function buildClientProfitabilityRows(
  projects: readonly ProjectProfitabilityEntry[],
  labourRatePerHour: number,
  completedTasks: readonly TimeLogEntry[] = [],
  activeTasks: readonly ActiveTaskEntry[] = [],
  allProjectsForTypeLookup: readonly ProjectProfitabilityEntry[] = projects
): ClientProfitabilityRow[] {
  const byClient = new Map<
    string,
    {
      projectCount: number
      largeProjectCount: number
      transactionalProjectCount: number
      invoiceTotal: number
      timeSpentMinutes: number
      latestCompletedDate: string | null
      projects: ClientProfitabilityProjectDetail[]
    }
  >()

  for (const project of projects) {
    const metrics = projectMarginMetricsForEntry(project, labourRatePerHour)
    if (metrics.marginPct == null || metrics.invoiceTotal <= 0) continue

    const client = project.client.trim()
    if (!client) continue

    const projectType = resolveMatrixDotProjectType(
      project,
      completedTasks,
      activeTasks,
      allProjectsForTypeLookup
    )

    let acc = byClient.get(client)
    if (!acc) {
      acc = {
        projectCount: 0,
        largeProjectCount: 0,
        transactionalProjectCount: 0,
        invoiceTotal: 0,
        timeSpentMinutes: 0,
        latestCompletedDate: null,
        projects: [],
      }
      byClient.set(client, acc)
    }

    acc.projectCount += 1
    if (projectType === 'Large Project') acc.largeProjectCount += 1
    else if (projectType === 'Transactional') acc.transactionalProjectCount += 1
    acc.invoiceTotal = Math.round((acc.invoiceTotal + metrics.invoiceTotal) * 100) / 100
    acc.timeSpentMinutes += project.timeSpentMinutes

    const completedDate = project.completedDate?.trim() ?? ''
    if (completedDate && (!acc.latestCompletedDate || completedDate > acc.latestCompletedDate)) {
      acc.latestCompletedDate = completedDate
    }

    acc.projects.push({
      id: project.id,
      projectNumber: resolveMatrixProjectNumber(project, completedTasks),
      projectTitle: project.projectTitle,
      invoiceTotal: metrics.invoiceTotal,
      hoursRecorded: metrics.hoursRecorded,
      profit: metrics.profit,
      marginPct: metrics.marginPct,
      projectType,
      completedDate,
      startDate: project.startDate?.trim() ?? '',
    })
  }

  const rows: ClientProfitabilityRow[] = []
  for (const [client, acc] of byClient) {
    const invoiceTotal = Math.round(acc.invoiceTotal * 100) / 100
    const costOfTime = projectLabourCostAtRate(acc.timeSpentMinutes, labourRatePerHour)
    const profit = Math.round((invoiceTotal - costOfTime) * 100) / 100
    const hoursRecorded = Math.round(decimalHoursFromMinutes(acc.timeSpentMinutes) * 10) / 10
    const marginPct = projectMarginPct(invoiceTotal, profit)

    acc.projects.sort(
      (a, b) =>
        b.invoiceTotal - a.invoiceTotal ||
        a.projectNumber.localeCompare(b.projectNumber)
    )

    rows.push({
      client,
      projectCount: acc.projectCount,
      largeProjectCount: acc.largeProjectCount,
      transactionalProjectCount: acc.transactionalProjectCount,
      invoiceTotal,
      hoursRecorded,
      costOfTime,
      profit,
      marginPct,
      latestCompletedDate: acc.latestCompletedDate,
      projects: acc.projects,
    })
  }

  return rows.sort(
    (a, b) => b.invoiceTotal - a.invoiceTotal || a.client.localeCompare(b.client)
  )
}

export function openTasksForEmployee(
  name: string,
  filter: 'all' | ProjectType,
  tasks: readonly ActiveTaskEntry[] = MOCK_ACTIVE_TASKS
): number {
  const matched = tasks.filter((t) => t.employeeName === name)
  if (filter === 'all') return matched.length
  return matched.filter((t) => t.projectType === filter).length
}

/** Task price ÷ time recorded ($/hr); null if no time recorded. */
export function hourlyRateFromActiveTask(taskPrice: number, timeRecordedHours: number): number | null {
  return hourlyRateFromTask(taskPrice, timeRecordedHours)
}

export function laborCostForEntry(entry: TimeLogEntry): number {
  return Math.round(entry.hoursWorked * entry.hourlyCost * 100) / 100
}

export function profitForEntry(entry: TimeLogEntry): number {
  return Math.round((entry.revenueGenerated - laborCostForEntry(entry)) * 100) / 100
}

/** Task price ÷ time recorded ($/hr); null if no time recorded. */
export function hourlyRateFromTask(taskPrice: number, timeRecordedHours: number): number | null {
  if (timeRecordedHours <= 0) return null
  return Math.round((taskPrice / timeRecordedHours) * 100) / 100
}
